import type { PublicationCode } from '@/scopes/common/domain/models'
import { mqMin, tok, tokPx } from '@sunwebgroup/ui-components'
import { Breadcrumbs, Typography } from '@sunwebgroup/ui-components/uds'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { Footer } from '@/scopes/common/components'
import { CatalogueContent } from '@/scopes/common/components/Footer/CatalogueContent'
import { Header } from '@/scopes/common/components/Header'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { OverlayBanner } from '../../common/components'
import { getBreadcrumbsConfig } from '../domain/policies/breadcrumbsConfig'
import marketingPreferencesUseCases from '../domain/useCases'
import marketingPreferencesApiClient from '../infrastructure/marketingPreferencesAPIClient'
import { marketingPreferencesStore } from '../infrastructure/store'
import { useMarketingPreferencesStore } from '../infrastructure/useMarketingPreferencesStore'
import { MarketingPreferencesForm } from './components/MarketingPreferencesForm'

const queryClient = new QueryClient()

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

const ContentWrapper = styled.div`
  background-color: ${tok('semantics_views_tinted_background_color')};
  flex-grow: 1;
`

const BreadcrumbsStyled = styled(Breadcrumbs)`
  padding-left: ${tokPx('primitives_space_16')};
  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding-left: 0;
  }
`

const Title = styled(Typography)`
  ${Typography.modVariant('display-g-regular')};
  margin-right: auto;
  padding-left: ${tokPx('primitives_space_16')};
  margin: ${tokPx('primitives_space_16')} 0;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Typography.modVariant('display-f-regular')};
    margin: ${tokPx('primitives_space_24')} 0;
    padding-left: 0;
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('display-e-regular')};
    margin-bottom: ${tokPx('primitives_space_32')};
  }
`

const Content = styled.div`
  max-width: ${tokPx('primitives_area_size_1440')};
  margin: 0 auto;
  color: ${tok('semantics_content_color')};

  padding-top: ${tokPx('primitives_space_24')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding: ${tokPx('primitives_space_24')} ${tokPx('primitives_space_32')} 0;
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding: ${tokPx('primitives_space_32')} ${tokPx('primitives_space_48')} 0;
  }
`

const FooterStyled = styled(Footer)`
  background-color: ${tok('semantics_views_tinted_background_color')};
`

export function MarketingPreferences() {
  const intl = useIntl()
  const useCases = React.useMemo(() => marketingPreferencesUseCases({
    apiClient: marketingPreferencesApiClient,
    store: marketingPreferencesStore,
  }), [])
  const isSaveSuccess = useMarketingPreferencesStore(state => state.savePreferences.success)
  const isSaveError = useMarketingPreferencesStore(state => state.savePreferences.error)
  const bannerColor: 'danger' | 'success' | undefined = isSaveError ? 'danger' : isSaveSuccess ? 'success' : undefined
  const bannerText = isSaveError
    ? intl.formatMessage({ id: 'save_error' })
    : isSaveSuccess
      ? intl.formatMessage({ id: 'save_success' })
      : undefined
  const banner = bannerText && bannerColor ? { color: bannerColor, text: bannerText } : undefined
  const publicationCode = useConfiguration('publicationCode') as PublicationCode
  const breadcrumbConfig = getBreadcrumbsConfig(publicationCode)
  return (
    <QueryClientProvider client={queryClient}>
      <Wrapper>
        <Header />
        <ContentWrapper>
          <Content>
            <BreadcrumbsStyled
              items={[
                { id: 'home', label: intl.formatMessage({ id: 'breadcrumb_home' }), href: breadcrumbConfig?.homeUrl },
                ...(breadcrumbConfig?.catalogUrl && breadcrumbConfig?.catalogTextId
                  ? [{ id: 'holiday', label: intl.formatMessage({
                      id: breadcrumbConfig.catalogTextId,
                    }), href: breadcrumbConfig.catalogUrl }]
                  : []),
                { id: 'marketing-preferences', label: intl.formatMessage({ id: 'breadcrumb_marketing_preferences' }) },
              ]}
            />
            {banner && <OverlayBanner color={banner.color} message={banner.text} />}
            <Title forwardedAs="h1">{intl.formatMessage({ id: 'marketing_preferences_title' })}</Title>
            <MarketingPreferencesForm useCases={useCases} />
          </Content>
        </ContentWrapper>
        <FooterStyled>
          <CatalogueContent />
        </FooterStyled>
      </Wrapper>
    </QueryClientProvider>
  )
}
